/**
 * PredatorField — tiered hostiles that hunt or flee the cluster.
 *
 * Ten tiers, named after real-world parasites and drawn to look like
 * them. Each predator's tier is fixed at spawn. Whether a given tier
 * is a threat or prey depends on the cluster's current cell count —
 * small clusters get hunted; big clusters hunt back. The agar.io loop:
 * avoid bigger parasites, eat smaller ones, eventually become apex.
 *
 * Threat (cluster ≤ tier.threatMax)
 *   homes toward the cluster, kills a cell on contact, gets a
 *   respawn delay after the strike.
 *
 * Prey (cluster ≥ tier.preyMin)
 *   flees the cluster, awards biomass on contact (the cluster
 *   eats it). Same respawn timer so the world stays populated.
 *
 * Visual tell: red halo when the predator is a threat, green halo
 * when it's prey. Body shape is unique per tier — a Toxoplasma is a
 * crescent, a Plasmodium is a signet ring inside a red cell, an
 * Anisakis is a coiled worm — so the codex entry has something to
 * point at. Halo colour and a tier-specific 'threat' / 'prey' tint
 * are the only role-dependent parts.
 */

import { Application, Container, Graphics } from 'pixi.js';

interface PredatorTier {
  name: string;
  /** Codex entry id; emitted as 'codex:unlocked' on first encounter. */
  codexId: string;
  /** Body radius in world px; halo + collision scale off this. */
  radius: number;
  driftSpeed: number;
  homeSpeed: number;
  senseRadius: number;
  /** Cluster size <= this: tier is a threat. */
  threatMax: number;
  /** Cluster size >= this: tier is prey. */
  preyMin: number;
  /** Biomass gained when eaten as prey. */
  biomass: number;
  bodyColorThreat: number;
  bodyColorPrey: number;
  respawnMin: number;
  respawnMax: number;
  /**
   * If set, this tier doesn't spawn until the cluster's biomass crosses
   * this threshold. Used for the late-game mega-predators (Behemoth,
   * Leviathan) — once you outgrow the apex, the world responds.
   */
  unlockBiomass?: number;
  /** Optional banner shown to the player on first unlock. */
  unlockTitle?: string;
  unlockSubtitle?: string;
  /**
   * Cells stripped from the cluster on a successful threat hit. Mega-
   * tiers chew through several cells per landed strike to make their
   * presence felt; default is 1 for the regular ladder.
   */
  cellsLost?: number;
}

// Ten parasites, named after real organisms so the codex entries can
// teach actual biology. Sizes ramp with the cluster's max cell count
// (~15) so each tier's threat/prey window lines up with a slice of
// growth — every few cell-grows, a new tier unlocks as edible.
const TIERS: PredatorTier[] = [
  { name: 'Toxoplasma',     codexId: 'parasite.toxoplasma',     radius: 7,  driftSpeed: 28, homeSpeed: 95,  senseRadius: 220,
    threatMax: 1,  preyMin: 2,  biomass: 800,
    bodyColorThreat: 0xc8504a, bodyColorPrey: 0xffd060,
    respawnMin: 5,  respawnMax: 9 },
  { name: 'Plasmodium',     codexId: 'parasite.plasmodium',     radius: 9,  driftSpeed: 30, homeSpeed: 110, senseRadius: 260,
    threatMax: 2,  preyMin: 3,  biomass: 2000,
    bodyColorThreat: 0x8a2a30, bodyColorPrey: 0xfdc060,
    respawnMin: 6,  respawnMax: 11 },
  { name: 'Giardia',        codexId: 'parasite.giardia',        radius: 11, driftSpeed: 26, homeSpeed: 120, senseRadius: 300,
    threatMax: 3,  preyMin: 4,  biomass: 5000,
    bodyColorThreat: 0xc04040, bodyColorPrey: 0xfde047,
    respawnMin: 7,  respawnMax: 12 },
  { name: 'Trypanosoma',    codexId: 'parasite.trypanosoma',    radius: 13, driftSpeed: 32, homeSpeed: 140, senseRadius: 340,
    threatMax: 4,  preyMin: 5,  biomass: 10000,
    bodyColorThreat: 0xc44848, bodyColorPrey: 0xb6f060,
    respawnMin: 8,  respawnMax: 14 },
  { name: 'Leishmania',     codexId: 'parasite.leishmania',     radius: 16, driftSpeed: 24, homeSpeed: 130, senseRadius: 380,
    threatMax: 6,  preyMin: 7,  biomass: 22000,
    bodyColorThreat: 0xc84a4a, bodyColorPrey: 0x9af07a,
    respawnMin: 10, respawnMax: 16 },
  { name: 'Cryptosporidium', codexId: 'parasite.cryptosporidium', radius: 19, driftSpeed: 22, homeSpeed: 135, senseRadius: 420,
    threatMax: 8,  preyMin: 9,  biomass: 45000,
    bodyColorThreat: 0xb86c44, bodyColorPrey: 0x7ce0a0,
    respawnMin: 14, respawnMax: 22 },
  { name: 'Babesia',        codexId: 'parasite.babesia',        radius: 22, driftSpeed: 20, homeSpeed: 145, senseRadius: 460,
    threatMax: 10, preyMin: 11, biomass: 80000,
    bodyColorThreat: 0xa8323a, bodyColorPrey: 0x6cd0c8,
    respawnMin: 18, respawnMax: 28 },
  { name: 'Schistosoma',    codexId: 'parasite.schistosoma',    radius: 27, driftSpeed: 18, homeSpeed: 150, senseRadius: 500,
    threatMax: 12, preyMin: 13, biomass: 150000,
    bodyColorThreat: 0x9a4a3c, bodyColorPrey: 0x60c8e0,
    respawnMin: 24, respawnMax: 36 },
  { name: 'Anisakis',       codexId: 'parasite.anisakis',       radius: 33, driftSpeed: 16, homeSpeed: 158, senseRadius: 540,
    threatMax: 13, preyMin: 14, biomass: 250000,
    bodyColorThreat: 0xd9b070, bodyColorPrey: 0x70a8ff,
    respawnMin: 30, respawnMax: 45 },
  { name: 'Cordyceps Apex', codexId: 'parasite.cordyceps',      radius: 42, driftSpeed: 14, homeSpeed: 165, senseRadius: 600,
    threatMax: 14, preyMin: 15, biomass: 400000,
    bodyColorThreat: 0x6a4a86, bodyColorPrey: 0xa890ff,
    respawnMin: 45, respawnMax: 70 },
  // --- Late-game mega-predators ---
  // Once the cluster outgrows Apex (~2M biomass), the existing tiers
  // are all prey. These two tiers exist to keep the world dangerous:
  // they're always threats — no matter how big the cluster gets, the
  // Lamprey Wraith and Tapeworm Titan are bigger and faster, so the
  // endgame stays a chase. preyMin is set absurdly high so the role
  // logic always returns 'threat'.
  { name: 'Lamprey Wraith', codexId: 'parasite.lamprey',        radius: 70, driftSpeed: 18, homeSpeed: 195, senseRadius: 760,
    threatMax: 99, preyMin: 999, biomass: 0,
    bodyColorThreat: 0x52384e, bodyColorPrey: 0x52384e,
    respawnMin: 18, respawnMax: 32,
    unlockBiomass: 500_000,
    unlockTitle: 'Something larger is hunting',
    unlockSubtitle: 'A Lamprey Wraith has emerged from the deep',
    cellsLost: 2 },
  { name: 'Tapeworm Titan', codexId: 'parasite.tapeworm',       radius: 95, driftSpeed: 14, homeSpeed: 178, senseRadius: 880,
    threatMax: 99, preyMin: 999, biomass: 0,
    bodyColorThreat: 0xb8a360, bodyColorPrey: 0xb8a360,
    respawnMin: 30, respawnMax: 55,
    unlockBiomass: 1_100_000,
    unlockTitle: 'The Titan stirs',
    unlockSubtitle: 'A Tapeworm Titan has surfaced — there is no outgrowing it',
    cellsLost: 3 },
];

// Base population per tier — what spawns at biomass 0. Numbers are
// tuned so the world feels populated even before the threat-density
// ramp kicks in. The notifyBiomass boost layered on top can multiply
// these by roughly 4× for the late tiers as biomass approaches 2M.
const POPULATION = [10, 8, 6, 6, 5, 4, 4, 4, 3, 3, 2, 2];

// Max additional copies the threat-density ramp will add to each tier
// once biomass crosses 2M. Higher tiers gain more so the late game
// piles on bigger threats most aggressively. Indexed by tier.
const TIER_EXTRA_AT_PEAK = [4, 4, 4, 4, 4, 5, 6, 6, 7, 8, 10, 10];

// Biomass at which the threat-density ramp tops out (progress = 1).
// Picked at 2M (well under the 3M stage goal) so the player feels the
// late-game predator pressure for the final stretch, not just at the
// moment they cross the goal line.
const THREAT_RAMP_TOP_BIOMASS = 2_000_000;

// Tiers whose body silhouette is elongated — those rotate to match
// heading instead of free-spinning while engaged. Worms and crescents
// look right swimming nose-first; symmetric bodies (rings, oocysts,
// blood cells, fungus stalks) look right free-spinning.
const ORIENTED: ReadonlyArray<boolean> = [
  true,  false, false, true,  true,
  false, false, true,  true,  false,
  true,  true,
];

const TURN_RATE = 0.6;
const WORLD_MARGIN = 140;

interface Predator {
  tier: number;
  x: number;
  y: number;
  heading: number;
  speed: number;
  state: 'drift' | 'home' | 'flee';
  pulsePhase: number;
  alive: boolean;
  respawnIn: number;
  /** Last-drawn role so we only re-render geometry on a flip. */
  drawnRole: 'threat' | 'prey' | 'none';
  g: Graphics;
}

export interface Strike {
  x: number;
  y: number;
  /** 'damage' = threat hit the cluster; 'prey' = cluster ate it. */
  type: 'damage' | 'prey';
  /** Biomass for 'prey'; 0 for 'damage'. */
  biomass: number;
  /** Display name of the tier — for floating text and codex entries. */
  name: string;
  /** Codex entry id, for `bus.emit('codex:unlocked')` on first contact. */
  codexId: string;
  /** Cells stripped on a 'damage' strike (mega-tiers > 1). 0 for prey. */
  cellsLost: number;
}

export class PredatorField {
  private parent: Container;
  private worldW: number;
  private worldH: number;
  private predators: Predator[] = [];
  private time = 0;
  private rand: () => number;
  /** Tiers whose units have been spawned. Mega-tiers (with
   *  unlockBiomass set) are added lazily once the threshold trips. */
  private spawnedTiers = new Set<number>();
  /**
   * Per-frame "newly unlocked" notifications, drained by the stage so
   * it can show a banner / play a cue. Avoids coupling the field to
   * the bus directly.
   */
  private pendingUnlocks: Array<{ name: string; title: string; subtitle: string }> = [];

  constructor(
    parent: Container,
    worldW: number,
    worldH: number,
    clusterX: number,
    clusterY: number,
  ) {
    this.parent = parent;
    this.worldW = worldW;
    this.worldH = worldH;
    let seed = 0x0BADBEEF;
    this.rand = () => {
      seed = (seed * 1664525 + 1013904223) | 0;
      return ((seed >>> 0) % 100000) / 100000;
    };
    for (let t = 0; t < TIERS.length; t++) {
      // Mega-tiers spawn lazily — see notifyBiomass.
      if (TIERS[t].unlockBiomass != null) continue;
      this.spawnTier(t, clusterX, clusterY);
    }
  }

  private spawnTier(t: number, clusterX: number, clusterY: number): void {
    if (this.spawnedTiers.has(t)) return;
    this.spawnedTiers.add(t);
    const tier = TIERS[t];
    const count = POPULATION[t];
    for (let i = 0; i < count; i++) {
      const g = new Graphics();
      this.parent.addChild(g);
      const [x, y] = this.pickSafeSpawn(clusterX, clusterY, tier.senseRadius * 1.6, this.rand);
      const p: Predator = {
        tier: t,
        x, y,
        heading: this.rand() * Math.PI * 2,
        speed: tier.driftSpeed,
        state: 'drift',
        pulsePhase: this.rand() * Math.PI * 2,
        alive: true,
        respawnIn: 0,
        drawnRole: 'none',
        g,
      };
      // Initial draw with a guess at role; will be corrected on the
      // first update tick.
      this.draw(p, 'threat');
      this.predators.push(p);
    }
  }

  /**
   * Called from the stage every frame with the current biomass + cluster
   * size. Two jobs:
   *  1. Spawn any unlock-gated tier whose biomass threshold has just
   *     been crossed (mega-predators) and queue a banner.
   *  2. Top up the population of currently-threat tiers as biomass
   *     ramps to 3M, so the late game keeps "things bigger than me"
   *     pressure on screen instead of trickling out as the cluster
   *     outgrows each tier.
   */
  notifyBiomass(biomass: number, clusterX: number, clusterY: number, clusterSize: number): void {
    // Despawn unlocked mega-tiers if biomass dropped back below their
    // threshold (player got hit, lost cells, biomass shrank). Hysteresis
    // at 90% prevents flicker if biomass jiggles right at the boundary.
    // Clearing spawnedTiers lets the unlock banner fire again if the
    // player re-grows past the threshold.
    for (let t = 0; t < TIERS.length; t++) {
      if (!this.spawnedTiers.has(t)) continue;
      const ub = TIERS[t].unlockBiomass;
      if (ub == null) continue;
      if (biomass < ub * 0.9) this.despawnTier(t);
    }

    for (let t = 0; t < TIERS.length; t++) {
      if (this.spawnedTiers.has(t)) continue;
      const ub = TIERS[t].unlockBiomass;
      if (ub != null && biomass >= ub) {
        this.spawnTier(t, clusterX, clusterY);
        const tier = TIERS[t];
        if (tier.unlockTitle && tier.unlockSubtitle) {
          this.pendingUnlocks.push({
            name: tier.name,
            title: tier.unlockTitle,
            subtitle: tier.unlockSubtitle,
          });
        }
      }
    }

    // Threat density ramp. progress goes 0→1 across [0, 2M] biomass.
    // Each tier gets a fixed peak-extra count from TIER_EXTRA_AT_PEAK;
    // we top up only when the tier is currently a threat (clusterSize
    // <= threatMax) so prey populations stay sensible.
    const progress = Math.max(0, Math.min(1, biomass / THREAT_RAMP_TOP_BIOMASS));
    if (progress <= 0) return;
    for (let t = 0; t < TIERS.length; t++) {
      if (!this.spawnedTiers.has(t)) continue;
      const tier = TIERS[t];
      if (clusterSize > tier.threatMax) continue;
      const target = POPULATION[t] + Math.ceil(TIER_EXTRA_AT_PEAK[t] * progress);
      const have = this.countOfTier(t);
      const need = target - have;
      for (let i = 0; i < need; i++) this.spawnExtraOfTier(t, clusterX, clusterY);
    }
  }

  private despawnTier(t: number): void {
    for (let i = this.predators.length - 1; i >= 0; i--) {
      const p = this.predators[i];
      if (p.tier !== t) continue;
      p.g.destroy();
      this.predators.splice(i, 1);
    }
    this.spawnedTiers.delete(t);
  }

  private countOfTier(t: number): number {
    let n = 0;
    for (const p of this.predators) if (p.tier === t) n++;
    return n;
  }

  /**
   * Add one more instance of a tier at runtime — same setup as the
   * initial spawnTier loop but doesn't touch spawnedTiers (the tier is
   * already marked spawned).
   */
  private spawnExtraOfTier(t: number, clusterX: number, clusterY: number): void {
    const tier = TIERS[t];
    const g = new Graphics();
    this.parent.addChild(g);
    const [x, y] = this.pickSafeSpawn(clusterX, clusterY, tier.senseRadius * 1.6, this.rand);
    const p: Predator = {
      tier: t,
      x, y,
      heading: this.rand() * Math.PI * 2,
      speed: tier.driftSpeed,
      state: 'drift',
      pulsePhase: this.rand() * Math.PI * 2,
      alive: true,
      respawnIn: 0,
      drawnRole: 'none',
      g,
    };
    this.draw(p, 'threat');
    this.predators.push(p);
  }

  /** Drain and return any tier-unlock notifications since the last call. */
  drainUnlocks(): Array<{ name: string; title: string; subtitle: string }> {
    if (this.pendingUnlocks.length === 0) return [];
    const out = this.pendingUnlocks;
    this.pendingUnlocks = [];
    return out;
  }

  private pickSafeSpawn(cx: number, cy: number, safeDist: number, rand: () => number): [number, number] {
    const ww = this.worldW;
    const wh = this.worldH;
    const safeSq = safeDist * safeDist;
    for (let i = 0; i < 24; i++) {
      const x = rand() * ww;
      const y = rand() * wh;
      const dx = x - cx;
      const dy = y - cy;
      if (dx * dx + dy * dy >= safeSq) return [x, y];
    }
    // Fallback: pick the world corner farthest from the cluster.
    const corners: Array<[number, number]> = [
      [WORLD_MARGIN, WORLD_MARGIN],
      [ww - WORLD_MARGIN, WORLD_MARGIN],
      [WORLD_MARGIN, wh - WORLD_MARGIN],
      [ww - WORLD_MARGIN, wh - WORLD_MARGIN],
    ];
    let best: [number, number] = corners[0];
    let bestD = -1;
    for (const c of corners) {
      const d = (c[0] - cx) ** 2 + (c[1] - cy) ** 2;
      if (d > bestD) { bestD = d; best = c; }
    }
    return best;
  }

  private roleFor(tier: number, clusterSize: number): 'threat' | 'prey' {
    return clusterSize <= TIERS[tier].threatMax ? 'threat' : 'prey';
  }

  private draw(p: Predator, role: 'threat' | 'prey'): void {
    p.g.clear();
    if (!p.alive) {
      p.drawnRole = 'none';
      return;
    }
    const tier = TIERS[p.tier];
    const r = tier.radius;
    const active = p.state !== 'drift';

    // Halo: red palette for threat, green for prey. Brighter when
    // actively engaged so the player can read intent at a glance.
    const haloColor = role === 'threat' ? 0xff5050 : 0x60ff90;
    const haloAlpha = active ? 0.26 : 0.13;
    p.g.circle(0, 0, r * 2.4).fill({ color: haloColor, alpha: haloAlpha * 0.5 });
    p.g.circle(0, 0, r * 1.7).fill({ color: haloColor, alpha: haloAlpha });

    const body = role === 'threat' ? tier.bodyColorThreat : tier.bodyColorPrey;
    // Accent for chromatin / kinetoplast / suckers: a deep tone in
    // both roles. Threats lean blood-red, prey lean ink-dark, so the
    // accent stays legible against the body color.
    const accent = role === 'threat' ? 0x2a0608 : 0x14201a;

    switch (p.tier) {
      case 0: this.drawToxoplasma(p.g, r, body, accent); break;
      case 1: this.drawPlasmodium(p.g, r, body, accent); break;
      case 2: this.drawGiardia(p.g, r, body, accent); break;
      case 3: this.drawTrypanosoma(p.g, r, body, accent); break;
      case 4: this.drawLeishmania(p.g, r, body, accent); break;
      case 5: this.drawCryptosporidium(p.g, r, body, accent); break;
      case 6: this.drawBabesia(p.g, r, body, accent); break;
      case 7: this.drawSchistosoma(p.g, r, body, accent); break;
      case 8: this.drawAnisakis(p.g, r, body, accent); break;
      case 9: this.drawCordyceps(p.g, r, body, accent); break;
      case 10: this.drawLamprey(p.g, r, body, accent); break;
      case 11: this.drawTapeworm(p.g, r, body, accent); break;
    }

    p.drawnRole = role;
  }

  /**
   * Toxoplasma — crescent-shaped tachyzoite. Real toxoplasma cells
   * are ~7µm long and shaped like a banana; the pointed end is the
   * 'apical complex' that drills into a host cell. We draw the
   * crescent with an outer arc traced one way and an inner arc back
   * the other way to leave a curved bite, then dot the apical tip.
   */
  private drawToxoplasma(g: Graphics, r: number, color: number, accent: number): void {
    const verts = 22;
    const R = r * 1.25;
    const inner = r * 0.95;
    const off = r * 0.55;
    let started = false;
    for (let i = 0; i <= verts; i++) {
      const a = Math.PI * 0.28 + (i / verts) * Math.PI * 1.44;
      const x = Math.cos(a) * R;
      const y = Math.sin(a) * R;
      if (!started) { g.moveTo(x, y); started = true; }
      else g.lineTo(x, y);
    }
    for (let i = 0; i <= verts; i++) {
      const a = Math.PI * 1.72 - (i / verts) * Math.PI * 1.44;
      g.lineTo(off + Math.cos(a) * inner, Math.sin(a) * inner);
    }
    g.closePath();
    g.fill({ color, alpha: 0.95 });
    g.stroke({ color: accent, width: 1.2, alpha: 0.6 });
    // Apical conoid (the pointed end that drills in).
    g.circle(-R + r * 0.05, -R * 0.55, r * 0.18).fill({ color: accent, alpha: 0.95 });
    // Pale nucleus toward the trailing end.
    g.circle(off * 0.4, R * 0.32, r * 0.22).fill({ color: 0xffffff, alpha: 0.45 });
  }

  /**
   * Plasmodium — drawn in its 'signet ring' trophozoite stage,
   * the form pathologists actually identify on a blood smear: a
   * thin ring with one dense chromatin dot, sitting inside a red
   * blood cell. We render the host RBC as a faint pinkish circle,
   * the parasite as an annulus (outer fill + smaller hole), and
   * the chromatin as a single dark bead on the rim.
   */
  private drawPlasmodium(g: Graphics, r: number, color: number, accent: number): void {
    g.circle(0, 0, r * 1.4).fill({ color: 0x9a2030, alpha: 0.32 });
    g.circle(0, 0, r * 1.4).stroke({ color: 0x661218, width: 1.5, alpha: 0.55 });
    g.circle(r * 0.15, r * 0.10, r * 0.95).fill({ color, alpha: 0.94 });
    g.circle(r * 0.15, r * 0.10, r * 0.55).fill({ color: 0x180404, alpha: 0.92 });
    g.circle(r * 0.85, -r * 0.18, r * 0.22).fill({ color: accent, alpha: 0.95 });
  }

  /**
   * Giardia — the famous 'smiley face' protozoan. Real Giardia
   * trophozoites are pear-shaped with two big nuclei (the 'eyes')
   * at the broad end and an adhesive disk that grips the gut wall
   * (the 'mouth'). Hard to forget once you've seen one under a
   * microscope; this draws it the same way.
   */
  private drawGiardia(g: Graphics, r: number, color: number, accent: number): void {
    const verts = 26;
    let started = false;
    for (let i = 0; i <= verts; i++) {
      const a = -Math.PI / 2 + (i / verts) * Math.PI * 2;
      // Pear: wide top (taper=1), narrow bottom (taper=0). Bottom
      // also pulled further down to make a teardrop point.
      const taper = 0.5 + 0.5 * Math.cos(a);
      const rad = r * (0.55 + taper * 0.55);
      const x = Math.cos(a) * rad;
      const y = Math.sin(a) * rad - (1 - taper) * r * 0.4;
      if (!started) { g.moveTo(x, y); started = true; }
      else g.lineTo(x, y);
    }
    g.closePath();
    g.fill({ color, alpha: 0.94 });
    g.stroke({ color: accent, width: 1.1, alpha: 0.5 });
    // Two nuclei — the 'eyes'.
    g.circle(-r * 0.38, -r * 0.42, r * 0.24).fill({ color: 0xfff4e8, alpha: 0.95 });
    g.circle( r * 0.38, -r * 0.42, r * 0.24).fill({ color: 0xfff4e8, alpha: 0.95 });
    g.circle(-r * 0.38, -r * 0.42, r * 0.11).fill({ color: accent });
    g.circle( r * 0.38, -r * 0.42, r * 0.11).fill({ color: accent });
    // Adhesive disk — the 'mouth'.
    g.ellipse(0, r * 0.1, r * 0.46, r * 0.16).fill({ color: accent, alpha: 0.55 });
    // Median bodies — two parallel dashes through the middle.
    g.rect(-r * 0.06, -r * 0.05, r * 0.12, r * 0.35).fill({ color: accent, alpha: 0.4 });
  }

  /**
   * Trypanosoma — long, slender, swims by undulating an attached
   * membrane. We draw a stretched ellipse for the body, a sine
   * wave along the top to suggest the undulating membrane, and a
   * trailing flagellum behind. Oriented to heading so it always
   * looks like it's swimming forward.
   */
  private drawTrypanosoma(g: Graphics, r: number, color: number, accent: number): void {
    g.ellipse(0, 0, r * 1.7, r * 0.42).fill({ color, alpha: 0.94 });
    g.ellipse(0, 0, r * 1.7, r * 0.42).stroke({ color: accent, width: 1.1, alpha: 0.55 });
    // Undulating membrane — a wavy stroke skimming above the body.
    const segs = 16;
    g.moveTo(-r * 1.7, 0);
    for (let i = 1; i <= segs; i++) {
      const t = i / segs;
      const x = -r * 1.7 + t * r * 3.4;
      const y = -Math.sin(t * Math.PI * 3) * r * 0.5;
      g.lineTo(x, y);
    }
    g.stroke({ color: 0xfff0c0, width: 1.5, alpha: 0.8 });
    // Kinetoplast (a unique DNA-rich organelle near one pole).
    g.circle(-r * 1.45, 0, r * 0.18).fill({ color: accent, alpha: 0.95 });
    // Nucleus — pale spot in the middle.
    g.circle(0, 0, r * 0.22).fill({ color: 0xffffff, alpha: 0.5 });
    // Flagellum trailing the back end.
    g.moveTo(r * 1.7, 0);
    for (let i = 1; i <= 8; i++) {
      const t = i / 8;
      const x = r * 1.7 + t * r * 1.1;
      const y = Math.sin(t * Math.PI * 2) * r * 0.28;
      g.lineTo(x, y);
    }
    g.stroke({ color: accent, width: 1.5, alpha: 0.85 });
  }

  /**
   * Leishmania — almond-shaped amastigote with one big kinetoplast
   * and a short flagellum. Real Leishmania amastigotes live inside
   * macrophages; the kinetoplast is so DNA-rich that pathologists
   * use it as the diagnostic feature. We draw an almond traced from
   * two arcs meeting at points, with the kinetoplast as a dense
   * dot off-center.
   */
  private drawLeishmania(g: Graphics, r: number, color: number, accent: number): void {
    const verts = 20;
    for (let i = 0; i <= verts; i++) {
      const t = i / verts;
      const x = -r * 1.1 + t * r * 2.2;
      const y = -Math.sin(t * Math.PI) * r * 0.55;
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    for (let i = 0; i <= verts; i++) {
      const t = i / verts;
      const x = r * 1.1 - t * r * 2.2;
      const y = Math.sin(t * Math.PI) * r * 0.55;
      g.lineTo(x, y);
    }
    g.closePath();
    g.fill({ color, alpha: 0.95 });
    g.stroke({ color: accent, width: 1.2, alpha: 0.55 });
    g.circle(-r * 0.55, 0, r * 0.20).fill({ color: accent, alpha: 0.95 });
    g.circle( r * 0.30, 0, r * 0.26).fill({ color: 0xffffff, alpha: 0.45 });
    g.moveTo(-r * 1.1, 0);
    for (let i = 1; i <= 6; i++) {
      const t = i / 6;
      const x = -r * 1.1 - t * r * 0.8;
      const y = Math.sin(t * Math.PI * 2) * r * 0.18;
      g.lineTo(x, y);
    }
    g.stroke({ color: accent, width: 1.4, alpha: 0.8 });
  }

  /**
   * Cryptosporidium — drawn as its tough oocyst (the form that
   * survives chlorine). Spherical with a thick double-layer wall
   * and four banana-shaped sporozoites packed inside in a pinwheel.
   * The wall is what makes Crypto so hard to kill: chemistry can't
   * touch it, only UV or ultra-fine filtration.
   */
  private drawCryptosporidium(g: Graphics, r: number, color: number, accent: number): void {
    g.circle(0, 0, r * 1.1).fill({ color: accent, alpha: 0.7 });
    g.circle(0, 0, r * 0.95).fill({ color, alpha: 0.95 });
    g.circle(0, 0, r * 0.78).fill({ color: 0xfff4e0, alpha: 0.18 });
    // Four sporozoites curled inside, oriented like a pinwheel.
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const cx = Math.cos(a) * r * 0.32;
      const cy = Math.sin(a) * r * 0.32;
      const verts = 10;
      let started = false;
      for (let j = 0; j <= verts; j++) {
        const t = j / verts;
        const aa = a + Math.PI / 2 - Math.PI * 0.55 + t * Math.PI * 1.1;
        const x = cx + Math.cos(aa) * r * 0.32;
        const y = cy + Math.sin(aa) * r * 0.32;
        if (!started) { g.moveTo(x, y); started = true; }
        else g.lineTo(x, y);
      }
      g.stroke({ color: accent, width: 1.6, alpha: 0.9 });
    }
    // Suture line — real oocysts split along a seam to release.
    g.moveTo(-r * 0.95, 0);
    g.lineTo( r * 0.95, 0);
    g.stroke({ color: accent, width: 1.0, alpha: 0.45 });
  }

  /**
   * Babesia — drawn as its diagnostic 'Maltese cross': four
   * pear-shaped merozoites arranged at the corners of a square
   * inside a host red blood cell. This tetrad is the thing that
   * tells a pathologist 'babesiosis, not malaria' on a blood
   * smear. Each merozoite has a dark chromatin pole.
   */
  private drawBabesia(g: Graphics, r: number, color: number, accent: number): void {
    g.circle(0, 0, r * 1.3).fill({ color: 0x9a2030, alpha: 0.32 });
    g.circle(0, 0, r * 1.3).stroke({ color: 0x661218, width: 1.5, alpha: 0.55 });
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const cx = Math.cos(a) * r * 0.42;
      const cy = Math.sin(a) * r * 0.42;
      // Pear-shaped merozoite oriented along radius.
      const len = r * 0.45;
      const wid = r * 0.22;
      const dx = Math.cos(a);
      const dy = Math.sin(a);
      const verts = 14;
      let started = false;
      for (let j = 0; j <= verts; j++) {
        const t = j / verts;
        const ang = t * Math.PI * 2;
        // Local pear coords: x along (-len, +len), pointed at +len.
        const lx = Math.cos(ang) * len * (1 - 0.3 * Math.cos(ang) * 0.5);
        const ly = Math.sin(ang) * wid * (1 - 0.4 * Math.cos(ang));
        const wx = cx + lx * dx - ly * dy;
        const wy = cy + lx * dy + ly * dx;
        if (!started) { g.moveTo(wx, wy); started = true; }
        else g.lineTo(wx, wy);
      }
      g.closePath();
      g.fill({ color, alpha: 0.93 });
      g.stroke({ color: accent, width: 1.0, alpha: 0.55 });
      // Chromatin dot at the outer end of each pear.
      g.circle(cx + dx * len * 0.65, cy + dy * len * 0.65, r * 0.10).fill({ color: accent, alpha: 0.95 });
    }
  }

  /**
   * Schistosoma — drawn as a permanently-paired couple, the way
   * the worms actually live. The male is broader and curves
   * around the female, who lies in his 'gynaecophoric canal'.
   * Two anterior suckers at the mouth end. Oriented to heading.
   */
  private drawSchistosoma(g: Graphics, r: number, color: number, accent: number): void {
    // Male — broad body wrapping around the female.
    g.ellipse(0, 0, r * 1.7, r * 0.6).fill({ color, alpha: 0.92 });
    g.ellipse(0, 0, r * 1.7, r * 0.6).stroke({ color: accent, width: 1.1, alpha: 0.55 });
    // Gynaecophoric canal — darker groove the female sits in.
    g.ellipse(0, -r * 0.04, r * 1.4, r * 0.30).fill({ color: 0x000000, alpha: 0.28 });
    // Female — paler, thinner worm tucked in the canal.
    g.ellipse(0, -r * 0.04, r * 1.55, r * 0.18).fill({ color: 0xf0e0c0, alpha: 0.95 });
    g.ellipse(0, -r * 0.04, r * 1.55, r * 0.18).stroke({ color: accent, width: 0.9, alpha: 0.5 });
    // Oral and ventral suckers at the head.
    g.circle(-r * 1.55, 0, r * 0.20).fill({ color: accent, alpha: 0.9 });
    g.circle(-r * 1.55, 0, r * 0.10).fill({ color: 0x000000, alpha: 0.85 });
    g.circle(-r * 1.20, r * 0.32, r * 0.13).fill({ color: accent, alpha: 0.85 });
    // Posterior taper.
    g.ellipse(r * 1.55, 0, r * 0.28, r * 0.14).fill({ color, alpha: 0.92 });
  }

  /**
   * Anisakis — coiled nematode worm. Drawn as a single thick
   * stroke traced through a sinuous spiral, with a thinner pale
   * highlight along the middle to suggest a translucent cuticle.
   * A small dark mouth-ring at the head end. Oriented to heading
   * so it swims like a snake.
   */
  private drawAnisakis(g: Graphics, r: number, color: number, accent: number): void {
    const segments = 28;
    const length = r * 2.8;
    const startX = -length / 2;
    g.moveTo(startX, 0);
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const x = startX + t * length;
      const y = Math.sin(t * Math.PI * 2.4) * r * 0.55;
      g.lineTo(x, y);
    }
    g.stroke({ color, width: r * 0.55, alpha: 0.95, cap: 'round', join: 'round' });
    // Pale highlight stroke for translucent cuticle look.
    g.moveTo(startX, 0);
    for (let i = 1; i <= segments; i++) {
      const t = i / segments;
      const x = startX + t * length;
      const y = Math.sin(t * Math.PI * 2.4) * r * 0.55;
      g.lineTo(x, y);
    }
    g.stroke({ color: 0xfff8e0, width: r * 0.16, alpha: 0.55, cap: 'round' });
    // Head capsule with a black mouth ring.
    g.circle(startX, 0, r * 0.32).fill({ color: accent, alpha: 0.92 });
    g.circle(startX, 0, r * 0.16).fill({ color: 0x000000, alpha: 0.95 });
    // Tail tip — slight bulge.
    g.circle(startX + length, Math.sin(Math.PI * 2.4) * r * 0.55, r * 0.20).fill({ color, alpha: 0.95 });
  }

  /**
   * Cordyceps Apex — the parasite that controls behaviour. Drawn
   * as a fungal stalk with a bulbous fruiting body releasing
   * spores, on top of a host body with radiating hyphae. The
   * single central eye is a nod to the way Ophiocordyceps drives
   * its host while the host is still alive.
   */
  private drawCordyceps(g: Graphics, r: number, color: number, accent: number): void {
    // Hyphae — thin tendrils radiating outward from the host body.
    for (let i = 0; i < 10; i++) {
      const a = (i / 10) * Math.PI * 2;
      g.moveTo(0, 0);
      g.lineTo(Math.cos(a) * r * 1.6, Math.sin(a) * r * 1.6);
    }
    g.stroke({ color: accent, width: 1.4, alpha: 0.55 });
    // Host body.
    g.circle(0, 0, r * 0.95).fill({ color, alpha: 0.94 });
    g.circle(0, 0, r * 0.95).stroke({ color: accent, width: 1.4, alpha: 0.7 });
    // Stalk emerging from the top.
    g.rect(-r * 0.16, -r * 1.5, r * 0.32, r * 1.0).fill({ color: 0xb8a070, alpha: 0.92 });
    g.rect(-r * 0.16, -r * 1.5, r * 0.32, r * 1.0).stroke({ color: accent, width: 1.0, alpha: 0.6 });
    // Bulbous fruiting head.
    g.circle(0, -r * 1.6, r * 0.55).fill({ color: 0xe8d098, alpha: 0.96 });
    g.circle(0, -r * 1.6, r * 0.55).stroke({ color: accent, width: 1.2, alpha: 0.7 });
    // Spore puffs around the head.
    for (let i = 0; i < 5; i++) {
      const a = -Math.PI + (i / 4) * Math.PI;
      g.circle(Math.cos(a) * r * 0.85, -r * 1.6 + Math.sin(a) * r * 0.85, r * 0.13)
        .fill({ color: 0xfff4c8, alpha: 0.7 });
    }
    // Central eye — the parasite watching from inside the host.
    g.circle(0, 0, r * 0.34).fill({ color: 0xffffff, alpha: 0.55 });
    g.circle(0, 0, r * 0.18).fill({ color: accent, alpha: 0.95 });
  }

  /**
   * Lamprey Wraith — long jawless predator with a gaping circular
   * sucker mouth. Drawn as an elongated tapered body, a wide round
   * mouth at the head with concentric tooth rings, and a wavy tail
   * fin. Oriented to heading so it always swims nose-first.
   */
  private drawLamprey(g: Graphics, r: number, color: number, accent: number): void {
    // Tapered eel body — wide near the head, narrowing to the tail.
    const verts = 28;
    const len = r * 2.6;
    let started = false;
    for (let i = 0; i <= verts; i++) {
      const t = i / verts;
      const x = -len + t * len * 2;
      // Width tapers from full at head (-len) to thin at tail (+len).
      const w = r * (0.85 - t * 0.55);
      if (!started) { g.moveTo(x, -w); started = true; }
      else g.lineTo(x, -w);
    }
    for (let i = verts; i >= 0; i--) {
      const t = i / verts;
      const x = -len + t * len * 2;
      const w = r * (0.85 - t * 0.55);
      g.lineTo(x, w);
    }
    g.closePath();
    g.fill({ color, alpha: 0.95 });
    g.stroke({ color: accent, width: 2, alpha: 0.65 });

    // Tail fin — wedge spread at the tail tip.
    g.moveTo(len * 0.95, 0);
    g.lineTo(len * 1.35, -r * 0.55);
    g.lineTo(len * 1.20, 0);
    g.lineTo(len * 1.35, r * 0.55);
    g.closePath();
    g.fill({ color, alpha: 0.85 });
    g.stroke({ color: accent, width: 1.5, alpha: 0.6 });

    // Sucker mouth — the horror feature. A black gaping ring with two
    // concentric tooth circles (small wedges around the rim).
    const mx = -len + r * 0.05;
    g.circle(mx, 0, r * 0.85).fill({ color: 0x080608, alpha: 0.95 });
    g.circle(mx, 0, r * 0.85).stroke({ color: accent, width: 2, alpha: 0.9 });
    g.circle(mx, 0, r * 0.55).stroke({ color: 0x4a1a1a, width: 1.2, alpha: 0.85 });
    // Tooth ring — small triangular pegs around the inner rim.
    const teeth = 14;
    for (let i = 0; i < teeth; i++) {
      const a = (i / teeth) * Math.PI * 2;
      const x0 = mx + Math.cos(a) * r * 0.78;
      const y0 = Math.sin(a) * r * 0.78;
      const x1 = mx + Math.cos(a) * r * 0.50;
      const y1 = Math.sin(a) * r * 0.50;
      g.moveTo(x0, y0).lineTo(x1, y1);
    }
    g.stroke({ color: 0xfff0e0, width: 1.4, alpha: 0.85 });

    // Eyes — small dark slits behind the mouth.
    g.circle(-len + r * 1.1, -r * 0.35, r * 0.10).fill({ color: 0xffd060, alpha: 0.95 });
    g.circle(-len + r * 1.1,  r * 0.35, r * 0.10).fill({ color: 0xffd060, alpha: 0.95 });
    g.circle(-len + r * 1.1, -r * 0.35, r * 0.05).fill({ color: 0x000000 });
    g.circle(-len + r * 1.1,  r * 0.35, r * 0.05).fill({ color: 0x000000 });

    // Lateral line — pale stripe down the body.
    g.moveTo(-len + r * 1.2, 0);
    g.lineTo(len * 0.95, 0);
    g.stroke({ color: 0xfff8d0, width: 1.2, alpha: 0.35 });
  }

  /**
   * Tapeworm Titan — segmented flatworm rendered as a long chain of
   * proglottids. The head (scolex) carries a ring of hooks and four
   * suckers — the gripping crown that anchors a real tapeworm to the
   * gut wall. Body trails in segments of alternating shade. Oriented
   * to heading so it slithers along its length.
   */
  private drawTapeworm(g: Graphics, r: number, color: number, accent: number): void {
    const segCount = 14;
    const segLen = r * 0.42;
    const segW = r * 0.62;
    const length = segCount * segLen;
    // Segments — chain of rounded rectangles trailing from head.
    for (let i = 0; i < segCount; i++) {
      // Slight S-curve along the body so it doesn't read as a stick.
      const baseX = -length / 2 + i * segLen + segLen * 0.5;
      const wobble = Math.sin(i * 0.6) * r * 0.14;
      const w = segW * (1.0 - i * 0.025);
      // Alternate slightly darker tones so the segmentation reads.
      const tone = i % 2 === 0 ? color : 0x9c8650;
      g.roundRect(baseX - segLen / 2, wobble - w / 2, segLen, w, segLen * 0.45)
        .fill({ color: tone, alpha: 0.95 })
        .stroke({ color: accent, width: 1.2, alpha: 0.55 });
    }

    // Scolex (head) — a bulb wider than the segments, with a ring of
    // hooks at the front and four ventral suckers.
    const hx = -length / 2 - segLen * 0.5;
    g.circle(hx, 0, r * 0.78).fill({ color, alpha: 0.96 });
    g.circle(hx, 0, r * 0.78).stroke({ color: accent, width: 1.6, alpha: 0.7 });
    // Four suckers (one in each quadrant of the scolex).
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const sx = hx + Math.cos(a) * r * 0.42;
      const sy = Math.sin(a) * r * 0.42;
      g.circle(sx, sy, r * 0.18).fill({ color: 0x1a1208, alpha: 0.92 });
      g.circle(sx, sy, r * 0.10).fill({ color: 0x4a3818, alpha: 0.95 });
    }
    // Hook crown — radial spikes around the front of the scolex.
    const hooks = 12;
    for (let i = 0; i < hooks; i++) {
      const a = -Math.PI / 2 + (i / hooks) * Math.PI;
      const x0 = hx + Math.cos(a) * r * 0.78;
      const y0 = Math.sin(a) * r * 0.78;
      const x1 = hx + Math.cos(a) * r * 1.05;
      const y1 = Math.sin(a) * r * 1.05;
      g.moveTo(x0, y0).lineTo(x1, y1);
    }
    g.stroke({ color: 0xfff4d0, width: 2.0, alpha: 0.9 });

    // Tail tip — taper at the back.
    const tx = length / 2 + segLen * 0.2;
    g.circle(tx, 0, r * 0.18).fill({ color: 0x8c7244, alpha: 0.9 });
  }

  update(dt: number, clusterX: number, clusterY: number, clusterSize: number): void {
    this.time += dt;
    const ww = this.worldW;
    const wh = this.worldH;

    for (const p of this.predators) {
      const tier = TIERS[p.tier];
      if (!p.alive) {
        p.respawnIn -= dt;
        if (p.respawnIn <= 0) {
          const [x, y] = this.pickSafeSpawn(clusterX, clusterY, tier.senseRadius * 1.6, Math.random);
          p.x = x;
          p.y = y;
          p.heading = Math.random() * Math.PI * 2;
          p.alive = true;
          p.state = 'drift';
          this.draw(p, this.roleFor(p.tier, clusterSize));
        }
        continue;
      }

      const role = this.roleFor(p.tier, clusterSize);
      const dx = clusterX - p.x;
      const dy = clusterY - p.y;
      const d = Math.hypot(dx, dy);

      // Sense: drift → engaged inside sense radius, engaged → drift
      // outside 1.5× (hysteresis to avoid flicker at the boundary).
      // Engaged means home (threat) or flee (prey).
      const senseR = tier.senseRadius;
      const engageState: 'home' | 'flee' = role === 'threat' ? 'home' : 'flee';
      if (p.state === 'drift' && d < senseR) p.state = engageState;
      else if ((p.state === 'home' || p.state === 'flee') && d > senseR * 1.5) p.state = 'drift';
      // Role flipped (e.g., player just grew past threshold): reset
      // engagement so a fleeing prey doesn't stay in 'flee' as a threat.
      if ((role === 'threat' && p.state === 'flee') || (role === 'prey' && p.state === 'home')) {
        p.state = engageState;
      }

      if ((p.state === 'home' || p.state === 'flee') && d > 1) {
        // Flee = steer AWAY from cluster (negate the direction vector).
        const sign = p.state === 'home' ? 1 : -1;
        const want = Math.atan2(dy * sign, dx * sign);
        let diff = want - p.heading;
        while (diff > Math.PI) diff -= Math.PI * 2;
        while (diff < -Math.PI) diff += Math.PI * 2;
        p.heading += diff * Math.min(1, 4 * dt);
        p.speed = tier.homeSpeed;
      } else {
        p.heading += (Math.random() - 0.5) * 2 * TURN_RATE * dt;
        p.speed = tier.driftSpeed;
        const nearWall =
          p.x < WORLD_MARGIN || p.x > ww - WORLD_MARGIN ||
          p.y < WORLD_MARGIN || p.y > wh - WORLD_MARGIN;
        if (nearWall) {
          const want = Math.atan2(wh * 0.5 - p.y, ww * 0.5 - p.x);
          let diff = want - p.heading;
          while (diff > Math.PI) diff -= Math.PI * 2;
          while (diff < -Math.PI) diff += Math.PI * 2;
          p.heading += diff * Math.min(1, 2 * dt);
        }
      }

      p.x += Math.cos(p.heading) * p.speed * dt;
      p.y += Math.sin(p.heading) * p.speed * dt;
      if (p.x < 0) p.x = 0;
      else if (p.x > ww) p.x = ww;
      if (p.y < 0) p.y = 0;
      else if (p.y > wh) p.y = wh;

      // Re-render only on role change (or first draw after respawn).
      if (p.drawnRole !== role) this.draw(p, role);

      p.g.position.set(p.x, p.y);
      const pulse = 1 + Math.sin(this.time * 4 + p.pulsePhase) * 0.08;
      p.g.scale.set(pulse);
      // Elongated bodies face their swim direction; symmetric ones
      // free-spin a bit while engaged so they read as agitated.
      if (ORIENTED[p.tier]) {
        p.g.rotation = p.heading;
      } else if (p.state === 'home' || p.state === 'flee') {
        p.g.rotation += dt * (p.state === 'home' ? 1.6 : 2.2);
      }
    }
  }

  /**
   * First live predator in contact with the cluster's absorption sweep.
   * If the predator is a threat AND the cluster is in i-frames, we
   * skip it (the contact is "ignored" until i-frames end). Prey
   * contact always resolves — eating doesn't hurt.
   */
  collideWith(cx: number, cy: number, r: number, clusterSize: number, iframeActive: boolean): Strike | null {
    for (const p of this.predators) {
      if (!p.alive) continue;
      const tier = TIERS[p.tier];
      const role = this.roleFor(p.tier, clusterSize);
      if (role === 'threat' && iframeActive) continue;
      const reach = r + tier.radius * 0.85;
      const dx = p.x - cx;
      const dy = p.y - cy;
      if (dx * dx + dy * dy <= reach * reach) {
        const hit: Strike = {
          x: p.x, y: p.y,
          type: role === 'threat' ? 'damage' : 'prey',
          biomass: role === 'prey' ? tier.biomass : 0,
          name: tier.name,
          codexId: tier.codexId,
          cellsLost: role === 'threat' ? (tier.cellsLost ?? 1) : 0,
        };
        // Prey is consumed and respawns; threats survive the contact
        // and keep hunting. The cluster's i-frames + knockback (handled
        // by the stage) prevent the same threat from chain-hitting on
        // the next frame. A future DNA upgrade may unlock the ability
        // to kill threats by ramming.
        if (role === 'prey') {
          p.alive = false;
          p.respawnIn = tier.respawnMin + Math.random() * (tier.respawnMax - tier.respawnMin);
          p.g.clear();
          p.drawnRole = 'none';
        }
        return hit;
      }
    }
    return null;
  }

  /**
   * First live predator whose codexId matches and whose world position
   * falls inside the given screen rect. Returns world-space x/y plus
   * tier radius — caller is responsible for converting to screen space.
   */
  findVisibleByCodexId(
    codexId: string,
    camOffsetX: number,
    camOffsetY: number,
    screenW: number,
    screenH: number,
  ): { x: number; y: number; radius: number } | null {
    for (const p of this.predators) {
      if (!p.alive) continue;
      const tier = TIERS[p.tier];
      if (tier.codexId !== codexId) continue;
      const sx = p.x + camOffsetX;
      const sy = p.y + camOffsetY;
      // Margin scales with tier radius (+ reading buffer) so the
      // popup waits until the body is fully on-screen instead of
      // catching predators mid-entry.
      const m = tier.radius + 32;
      if (sx < m || sx > screenW - m || sy < m || sy > screenH - m) continue;
      return { x: p.x, y: p.y, radius: tier.radius };
    }
    return null;
  }

  /**
   * Render every predator tier into a static icon canvas keyed by codex
   * id. Same draw paths the in-game predator uses, so the codex entry
   * shows the exact silhouette the player meets in the sea. Threat
   * palette + a faint red halo for visual consistency with the live
   * predator's "threat" tint (most players' first encounter is as prey).
   */
  buildIconMap(app: Application): Map<string, HTMLCanvasElement> {
    const out = new Map<string, HTMLCanvasElement>();
    const SIZE = 56;
    for (let t = 0; t < TIERS.length; t++) {
      const tier = TIERS[t];
      // Cap radius so big tiers (Tapeworm Titan, r=95) still fit the
      // icon frame. Small tiers render at native size.
      const r = Math.min(18, tier.radius);
      const body = tier.bodyColorThreat;
      const accent = 0x2a0608;
      const c = new Container();
      const frame = new Graphics();
      frame.rect(-SIZE / 2, -SIZE / 2, SIZE, SIZE).fill({ color: 0, alpha: 0.001 });
      c.addChild(frame);
      const g = new Graphics();
      // Faint threat halo so the icon reads as "predator" even before
      // the body shape is recognized.
      g.circle(0, 0, r * 1.7).fill({ color: 0xff5050, alpha: 0.13 });
      switch (t) {
        case 0:  this.drawToxoplasma(g, r, body, accent); break;
        case 1:  this.drawPlasmodium(g, r, body, accent); break;
        case 2:  this.drawGiardia(g, r, body, accent); break;
        case 3:  this.drawTrypanosoma(g, r, body, accent); break;
        case 4:  this.drawLeishmania(g, r, body, accent); break;
        case 5:  this.drawCryptosporidium(g, r, body, accent); break;
        case 6:  this.drawBabesia(g, r, body, accent); break;
        case 7:  this.drawSchistosoma(g, r, body, accent); break;
        case 8:  this.drawAnisakis(g, r, body, accent); break;
        case 9:  this.drawCordyceps(g, r, body, accent); break;
        case 10: this.drawLamprey(g, r, body, accent); break;
        case 11: this.drawTapeworm(g, r, body, accent); break;
      }
      c.addChild(g);
      out.set(tier.codexId, app.renderer.extract.canvas(c) as unknown as HTMLCanvasElement);
    }
    return out;
  }

  /**
   * Wipe every predator on the field and re-seed the starting tiers
   * around the (just-respawned) cluster. Mega-tiers that unlocked at
   * higher biomass thresholds are forgotten — they'll re-unlock if the
   * player re-grows past those thresholds. Stops the situation where
   * a freshly-reborn 4-cell cluster spawns next to a Behemoth that was
   * roaming around when the previous run died.
   */
  resetForRebirth(clusterX: number, clusterY: number): void {
    for (const p of this.predators) p.g.destroy();
    this.predators = [];
    this.spawnedTiers.clear();
    this.pendingUnlocks.length = 0;
    for (let t = 0; t < TIERS.length; t++) {
      if (TIERS[t].unlockBiomass != null) continue;
      this.spawnTier(t, clusterX, clusterY);
    }
  }

  destroy(): void {
    for (const p of this.predators) p.g.destroy();
    this.predators = [];
  }
}
